import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import LookupForm from "@/components/LookupForm";
import ProgressLog from "@/components/ProgressLog";
import ReportViewer from "@/components/ReportViewer";
import EmptyState from "@/components/EmptyState";
import { Wordmark } from "@/components/BrandShapes";
import { DD } from "@/constants/testIds";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const POLL_INTERVAL_MS = 2000;
const POLL_HARD_TIMEOUT_MS = 12 * 60 * 1000; // 12 min — well above worst-case
const MAX_CONSECUTIVE_404 = 6;                // ~12 s of "job not found"
const MAX_CONSECUTIVE_ERR = 15;                // ~30 s of transient network errors

// status: "idle" | "running" | "done" | "error"
export default function Dashboard() {
    const [status, setStatus] = useState("idle");
    const [steps, setSteps] = useState([]); // [{label, status}]
    const [phase, setPhase] = useState(null); // "research" | "synthesis"
    const [report, setReport] = useState(null);
    const [meta, setMeta] = useState(null);
    const [errorMsg, setErrorMsg] = useState(null);
    const [jobId, setJobId] = useState(null);
    const cancelledRef = useRef(false);

    const stopPolling = useCallback(() => {
        cancelledRef.current = true;
        setJobId(null);
    }, []);

    const handleStart = useCallback(async ({ ngoName, country, countryName, regId, website, mode }) => {
        stopPolling();
        cancelledRef.current = false;
        setStatus("running");
        setPhase("research");
        setSteps([]);
        setReport(null);
        setErrorMsg(null);
        setMeta({ ngoName, country, countryName, regId, website, mode });

        try {
            const { data: job } = await axios.post(`${API}/dd/jobs`, {
                ngo_name: ngoName,
                country,
                country_name: countryName || "",
                reg_id: regId || "",
                website: website || "",
                mode: mode || "fast",
            }, { timeout: 30000 });
            if (!job?.job_id) {
                throw new Error("Server response missing job_id");
            }
            setJobId(job.job_id);
        } catch (e) {
            const detail = e?.response?.data?.detail
                || e?.response?.statusText
                || e?.message
                || "network error";
            const code = e?.response?.status;
            // eslint-disable-next-line no-console
            console.error("dd/jobs POST failed:", code, detail, e);
            setErrorMsg(`Could not start the research job (${code || "no-response"}: ${detail}). Please try again.`);
            setStatus("error");
        }
    }, [stopPolling]);

    // Polling loop — re-runs whenever a new jobId is assigned, fixing the
    // race where the previous code captured jobRef.current before the POST
    // had resolved and never restarted.
    useEffect(() => {
        if (status !== "running" || !jobId) return undefined;

        let cancelled = false;
        const startedAt = Date.now();
        let consecutive404 = 0;
        let consecutiveErr = 0;

        const fail = (message) => {
            setErrorMsg(message);
            setStatus("error");
            setJobId(null);
        };

        const tick = async () => {
            if (cancelled || cancelledRef.current) return;
            if (Date.now() - startedAt > POLL_HARD_TIMEOUT_MS) {
                fail("Timed out after 12 minutes. The synthesis step may still be running on the server — try again or check the backend logs.");
                return;
            }
            try {
                const { data } = await axios.get(`${API}/dd/jobs/${jobId}`);
                if (cancelled || cancelledRef.current) return;
                consecutive404 = 0;
                consecutiveErr = 0;
                setSteps(data.steps || []);
                setPhase(data.phase);

                if (data.phase === "done" && data.markdown) {
                    setReport(data.markdown);
                    setStatus("done");
                    setJobId(null);
                    return;
                }
                if (data.phase === "error") {
                    fail(data.error || "Pipeline failed.");
                    return;
                }
            } catch (e) {
                const code = e?.response?.status;
                if (code === 404) {
                    consecutive404 += 1;
                    if (consecutive404 >= MAX_CONSECUTIVE_404) {
                        fail("The server lost track of this job (it may have been restarted). Please start a new report.");
                        return;
                    }
                } else {
                    consecutiveErr += 1;
                    if (consecutiveErr >= MAX_CONSECUTIVE_ERR) {
                        fail("Lost connection to the backend. Please check your network and try again.");
                        return;
                    }
                }
            }
            setTimeout(tick, POLL_INTERVAL_MS);
        };

        const t = setTimeout(tick, POLL_INTERVAL_MS);
        return () => {
            cancelled = true;
            clearTimeout(t);
        };
    }, [status, jobId]);

    const handleReset = useCallback(() => {
        stopPolling();
        setStatus("idle");
        setSteps([]);
        setReport(null);
        setMeta(null);
        setErrorMsg(null);
        setPhase(null);
    }, [stopPolling]);

    // Optional sample-preview mode: ?sample=dkt-v4 → loads /sample-dd-report-<slug>.md
    // straight into the ReportViewer. Useful for demos without burning Tavily quota.
    // Optional resume mode: ?job=<jobId> → attach to an existing job and stream/show it.
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const jobParam = params.get("job");
        if (jobParam) {
            axios.get(`${API}/dd/jobs/${jobParam}`).then(({ data }) => {
                setSteps(data.steps || []);
                setPhase(data.phase);
                setMeta({
                    ngoName: data.ngo_name || "Report",
                    country: data.country || "",
                    countryName: data.country_name || "",
                    regId: data.reg_id || "",
                    website: data.website || "",
                });
                if (data.phase === "done" && data.markdown) {
                    setReport(data.markdown);
                    setStatus("done");
                } else if (data.phase === "error") {
                    setErrorMsg(data.error || "Pipeline failed.");
                    setStatus("error");
                } else {
                    setJobId(jobParam);
                    setStatus("running");
                }
            }).catch(() => {});
            return;
        }
        const slug = params.get("sample");
        if (!slug) return;
        const url = `/sample-dd-report-${slug.replace(/[^a-z0-9-]/gi, "")}.md`;
        fetch(url).then(async (r) => {
            if (!r.ok) return;
            const md = await r.text();
            setReport(md);
            setStatus("done");
            setMeta({ ngoName: slug.toUpperCase().replace(/-/g, " "), country: "—", regId: "" });
        }).catch(() => {});
    }, []);

    const headerSubtitle = useMemo(() => {
        if (status === "running") return "Research pipeline active";
        if (status === "done") return "Report ready";
        if (status === "error") return "Pipeline error";
        return "";
    }, [status]);

    return (
        <div className="flex h-screen overflow-hidden bg-ciff-paper font-body text-ciff-ink">
            {/* SIDEBAR */}
            <aside className="w-[360px] flex-shrink-0 border-r border-border bg-white flex flex-col h-full no-print">
                <div className="px-6 py-5 border-b border-border flex flex-col gap-3">
                    <Wordmark />
                    {headerSubtitle && (
                        <div className="text-[10px] uppercase tracking-[0.18em] text-ciff-inkSoft font-mono">
                            {headerSubtitle}
                        </div>
                    )}
                </div>

                <div className="flex-1 overflow-y-auto">
                    <LookupForm
                        status={status}
                        onStart={handleStart}
                        onReset={handleReset}
                    />
                    {status === "running" || status === "error" ? (
                        <ProgressLog
                            steps={steps}
                            phase={phase}
                            status={status}
                            errorMsg={errorMsg}
                        />
                    ) : null}
                </div>

                <div className="px-6 py-3 border-t border-border text-[10px] uppercase tracking-[0.18em] text-ciff-inkSoft font-mono">
                    Open-source · AI-assisted
                </div>
            </aside>

            {/* MAIN */}
            <main className="flex-1 flex flex-col h-full overflow-hidden">
                {status === "idle" && <EmptyState />}
                {(status === "running" || status === "error") && !report && (
                    <RunningState
                        meta={meta}
                        steps={steps}
                        phase={phase}
                        status={status}
                        errorMsg={errorMsg}
                    />
                )}
                {report && (
                    <ReportViewer
                        markdown={report}
                        meta={meta}
                        onReset={handleReset}
                    />
                )}
            </main>
        </div>
    );
}

// Icon + colour for a single research step. Pulled out of the render tree
// so the ternary chain doesn't sit inline inside JSX.
const STEP_ICON = { running: "…", error: "✗", done: "✓" };
const STEP_COLOR = {
    running: "hsl(var(--ciff-magenta))",
    error: "#f87171",
    done: "hsl(var(--ciff-magenta))",
};

function StepRow({ step }) {
    const status = step.status || "done";
    return (
        <div className="flex items-start gap-2 mt-1">
            <span
                style={{
                    color: STEP_COLOR[status] || STEP_COLOR.done,
                    opacity: status === "running" ? 0.7 : 1,
                }}
            >
                {STEP_ICON[status] || STEP_ICON.done}
            </span>
            <span className="opacity-90">{step.label}</span>
        </div>
    );
}

function RunningState({ meta, steps, phase, status, errorMsg }) {
    const [tick, setTick] = useState(0);
    const startedAtRef = useRef(Date.now());
    const synthesisStartRef = useRef(null);

    // 1-second ticker to drive the elapsed timer + animated synth progress
    useEffect(() => {
        const id = setInterval(() => setTick((t) => t + 1), 1000);
        return () => clearInterval(id);
    }, []);

    // Reset when a new run starts
    useEffect(() => {
        startedAtRef.current = Date.now();
        synthesisStartRef.current = null;
    }, [meta?.ngoName]);

    // Track when synthesis began (first tick where phase transitions)
    useEffect(() => {
        if (phase === "synthesis" && !synthesisStartRef.current) {
            synthesisStartRef.current = Date.now();
        }
    }, [phase]);

    const isDeep = meta?.mode === "deep";
    const synthEstMs = isDeep ? 6.5 * 60 * 1000 : 3.5 * 60 * 1000;

    const doneCount = steps.filter((s) => s.status === "done" || s.status === "error").length;
    const totalEstimate = Math.max(28, steps.length);
    const researchPct = Math.round((doneCount / totalEstimate) * 60); // research spans 0-60%

    let pct;
    let overrun = false;
    if (phase === "synthesis") {
        const elapsed = synthesisStartRef.current ? Date.now() - synthesisStartRef.current : 0;
        overrun = elapsed >= synthEstMs;
        // 60→94% while inside estimate; cap at 94% during overrun so we never
        // pretend the run is "about to finish" while it hasn't.
        const synthPct = Math.min(1, elapsed / synthEstMs) * 34;
        pct = 60 + synthPct;
    } else {
        pct = researchPct;
    }
    pct = Math.min(94, Math.max(2, Math.round(pct)));

    const elapsedTotal = Math.round((Date.now() - startedAtRef.current) / 1000);
    const fmt = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
    const remainingSec = synthesisStartRef.current
        ? Math.max(0, Math.round((synthEstMs - (Date.now() - synthesisStartRef.current)) / 1000))
        : 0;

    return (
        <div className="flex-1 flex flex-col overflow-hidden">
            <div className="px-10 py-6 border-b border-border bg-white">
                <div className="text-[11px] uppercase tracking-[0.18em] text-ciff-inkSoft font-mono">
                    Generating due diligence report · {isDeep ? "Deep (Sonnet 4.5)" : "Fast (Haiku 4.5)"}
                </div>
                <div className="font-heading text-3xl font-bold text-ciff-ink mt-1 tracking-tight">
                    {meta?.ngoName || "—"}
                </div>
                <div className="text-sm text-ciff-inkSoft mt-1 flex items-center gap-3 flex-wrap">
                    <span className="font-mono">{meta?.country || "—"}</span>
                    {meta?.regId ? <span className="font-mono">· {meta.regId}</span> : null}
                    <span className="text-ciff-inkSoft/50">·</span>
                    <span className="font-mono text-ciff-ink" data-testid="dd-elapsed-timer">
                        elapsed {fmt(elapsedTotal)}
                    </span>
                    {phase === "synthesis" && !overrun && (
                        <span className="font-mono text-ciff-inkSoft">
                            (est. {fmt(remainingSec)} remaining)
                        </span>
                    )}
                    {phase === "synthesis" && overrun && (
                        <span className="font-mono text-ciff-magenta">
                            finalising output…
                        </span>
                    )}
                </div>
                {/* Progress bar: solid fill + animated shimmer overlay during synthesis */}
                <div className="relative h-[4px] w-full bg-ciff-paper2 mt-5 overflow-hidden rounded-full">
                    <div
                        className={`h-full transition-all duration-500 ${status === "error" ? "bg-rose-600" : "bg-ciff-magenta"}`}
                        style={{ width: `${pct}%` }}
                    />
                    {phase === "synthesis" && status !== "error" && (
                        <div
                            className="absolute inset-0 h-full pointer-events-none"
                            style={{
                                background: "linear-gradient(90deg, transparent 0%, rgba(225,21,110,0.35) 50%, transparent 100%)",
                                backgroundSize: "200% 100%",
                                animation: "shimmer 1.8s linear infinite",
                            }}
                        />
                    )}
                </div>
                <style>{`@keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }`}</style>
            </div>

            <div className="flex-1 overflow-y-auto px-10 py-8 bg-ciff-paper">
                <div className="max-w-3xl mx-auto">
                    <div className="text-[11px] uppercase tracking-[0.18em] text-ciff-inkSoft font-mono mb-3">
                        Pipeline log
                    </div>
                    <div className="bg-ciff-ink text-ciff-paper rounded-sm p-5 font-mono text-[12.5px] leading-6 shadow-sm">
                        <div style={{ color: "hsl(var(--ciff-magenta))" }}>
                            $ ciff-dd research --ngo &quot;{meta?.ngoName}&quot; --country {meta?.country} --mode {meta?.mode || "fast"}
                        </div>
                        {steps.map((s, i) => (
                            <StepRow key={`${s.label}-${i}`} step={s} />
                        ))}
                        {phase === "synthesis" && (
                            <div className="mt-3" style={{ color: "hsl(var(--ciff-magenta))" }}>
                                → Synthesising report with {isDeep ? "Claude Sonnet 4.5" : "Claude Haiku 4.5"} — writing ~9,000 words + 400 citations
                                <span className="ml-1 inline-block w-2 h-3 align-middle animate-caret-blink" style={{ background: "hsl(var(--ciff-magenta))" }} />
                            </div>
                        )}
                        {status === "error" && (
                            <div className="mt-3 text-rose-400" data-testid="dd-error-banner">
                                ✗ Pipeline error: {errorMsg}
                            </div>
                        )}
                    </div>

                    <p className="text-xs text-ciff-inkSoft mt-4 leading-relaxed">
                        Searches run against Tavily across regulator filings, sanctions
                        lists, news media, and leadership profiles. Synthesis is
                        performed by {isDeep ? "Claude Sonnet 4.5" : "Claude Haiku 4.5"} using the gathered open-source
                        evidence as its dossier.
                    </p>
                </div>
            </div>
        </div>
    );
}
